// MissionSessionService.ts — Sprint 104: Mission Session Intelligence™
// BUILD: 2.3.0
//
// SINGLE RESPONSIBILITY:
//   Permanently record every interaction between the operator and a mission source.
//   Companies aggregate sessions. Dashboards aggregate companies.
//   Learning aggregates sessions. Nothing is overwritten. Nothing is deleted.
//
// ARCHITECTURE RULE:
//   Pure TypeScript — no React, no fetch, no external imports.
//   localStorage persistence. Storage key: nomad:sessions:v1.
//   All statistics are DERIVED from historical sessions — none are manually edited.
//   Companies OWN history. Sessions OWN intelligence.
//
// DO NOT MODIFY:
//   Decision Engine · Trust Engine · Ranking · Mission Planner · Mission Director ·
//   Harvester · Mission Source Registry · Business Logic.
//   Only add Mission Session Intelligence.

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Lifecycle status of a MissionSession.
 *
 * Sprint 105.2 corrects the lifecycle to record reality, not assumptions.
 * Opening a website ONLY creates REVIEWING — never WORKING or STARTED.
 *
 * Lifecycle: REVIEWING → INTERESTED → APPLYING → WAITING → WORKING
 *         → SUBMITTED → PAID → COMPLETED
 *
 * New statuses are additive — existing statuses are kept for backward compat.
 */
export type MissionSessionStatus =
  | 'Created'
  | 'Pre-Mission'       // legacy — kept for backward compat with existing sessions
  // ── Sprint 105.2: correct lifecycle states ─────────────────────────────────
  | 'Reviewing'         // Website visited — operator may be exploring, reading, deciding
  | 'Interested'        // Operator intends to continue (confirmed interest)
  | 'Applying'          // Creating account, completing onboarding, taking qualification tests
  | 'Waiting'           // Waiting for approval, invitation, verification, or available work
  | 'Submitted'         // Work has been submitted (terminal)
  | 'Paid'              // Confirmed paid work (terminal, positive)
  | 'Not Interested'    // Operator decided not to proceed (terminal, NOT a failure)
  // ── Existing statuses kept intact ──────────────────────────────────────────
  | 'Working'
  | 'Paused'
  | 'Resumed'
  | 'Completed'
  | 'Did Not Qualify'
  | 'No Work Available'
  | 'Applied'
  | 'Technical Problem'
  | 'Abandoned';

/**
 * Terminal statuses — no further updates are expected after these.
 *
 * Sprint 105.2 additions:
 *   'Submitted'    — work delivered, awaiting result (terminal)
 *   'Paid'         — payment confirmed (terminal, positive)
 *   'Not Interested' — operator opted out; never counts as failed work
 *
 * NON-terminal new states (operator may still be active):
 *   'Reviewing', 'Interested', 'Applying', 'Waiting'
 */
export const TERMINAL_STATUSES: MissionSessionStatus[] = [
  'Completed',
  'Did Not Qualify',
  'No Work Available',
  'Applied',
  'Technical Problem',
  'Abandoned',
  // Sprint 105.2 terminals:
  'Submitted',
  'Paid',
  'Not Interested',
];

/** The operator's self-reported device. Can be overridden from auto-detect. */
export type SessionDevice = 'Mobile' | 'Laptop' | 'Desktop' | 'Tablet' | 'Unknown';

/** Operator's self-reported emotional state at session start. */
export type OperatorMood = 'Focused' | 'Tired' | 'Just browsing' | 'Highly motivated';

/** Operator's self-reported available time at session start. */
export type TimeAvailable = '15 min' | '30 min' | '1 hour' | '2+ hours';

/** Payment timing as reported in post-mission questionnaire. */
export type PaymentStatus = 'Immediate' | 'Pending' | 'Unknown' | 'Not Applicable';

/** Type of work performed during the session. */
export type MissionSessionType =
  | 'UX Research'
  | 'AI Training'
  | 'User Testing'
  | 'Survey'
  | 'Focus Group'
  | 'App Testing'
  | 'Annotation'
  | 'Writing'
  | 'Other'
  | 'Unknown';

/** The operator's response to "Would you do this mission again?" */
export type WouldDoAgainResponse = 'Definitely' | 'Maybe' | 'No';

/** How clear the platform instructions were. */
export type InstructionClarity = 'Yes' | 'Somewhat' | 'No';

// ─── Core entity ──────────────────────────────────────────────────────────────

/**
 * MissionSession — the permanent record of one operator interaction with a mission source.
 *
 * One company may contain thousands of sessions.
 * Every time the operator opens, attempts, performs, or completes work,
 * a new session is created. Nothing is overwritten. Everything becomes
 * historical intelligence.
 *
 * Auto-captured fields are computed at creation/completion without any
 * operator input. They are marked with [AUTO] in the comments.
 */
export interface MissionSession {
  // ── Identity ──────────────────────────────────────────────────────────────
  /** UUID-style unique identifier. Never changes after creation. */
  sessionId: string;

  /**
   * Normalised company identifier (lowercase trim of company name).
   * Matches the platform key used by operatorLearningEngine.ts.
   */
  companyId: string | null;

  /** Raw display name of the company. Stored for readability. */
  companyName: string | null;

  /** NOMAD opportunity ID this session is associated with (nullable for manual visits). */
  opportunityId: number | null;

  /** Operator identifier. From operator profile or 'operator' default. */
  operatorId: string;

  // ── Timeline ──────────────────────────────────────────────────────────────
  /** ISO 8601 — when session was created (pre-mission questionnaire opened). */
  startedAt: string;

  /** ISO 8601 — when the session reached a terminal status. Null until then. */
  endedAt: string | null;

  /**
   * Actual elapsed minutes.
   * Auto-computed from timer on complete; overridable by operator in post-mission form.
   */
  durationMinutes: number | null;

  // ── Status ────────────────────────────────────────────────────────────────
  status: MissionSessionStatus;

  // ── Financial ─────────────────────────────────────────────────────────────
  /** USD actually earned. Null until post-mission form is saved. */
  earningsActual: number | null;

  /** USD estimated from the opportunity listing. Auto-populated at creation. */
  earningsEstimated: number | null;

  /** [AUTO] Computed on completion: earningsActual / (durationMinutes / 60). */
  actualHourlyRate: number | null;

  paymentStatus: PaymentStatus;

  /** ISO 8601 date — when payment was received or is expected. */
  paymentReceivedDate: string | null;

  // ── Outcome ───────────────────────────────────────────────────────────────
  /** Whether the operator qualified for the work. */
  qualificationResult: MissionSessionStatus;

  missionType: MissionSessionType;

  // ── Device & context ──────────────────────────────────────────────────────
  /**
   * Self-reported by operator in pre-mission questionnaire.
   * Auto-detected as initial value but operator can override.
   */
  deviceUsed: SessionDevice;

  /** Optional physical location (city, country). Operator-entered or omitted. */
  location: string | null;

  /** Operator's mood at session start. From pre-mission questionnaire. */
  preSessionMood: OperatorMood | null;

  /** Operator's stated available time. From pre-mission questionnaire. Minutes. */
  timeAvailableMinutes: number | null;

  /** Whether the expected earnings were acceptable to the operator. From pre-mission form. */
  earningsExpectationMet: boolean | null;

  // ── Subjective quality scores ─────────────────────────────────────────────
  /** How frustrating or difficult the process was (1=smooth, 5=very frustrating). */
  frictionScore: number | null;

  /** How difficult the work itself was (1=easy, 5=very hard). */
  difficultyScore: number | null;

  /** Overall satisfaction with the session (1=terrible, 5=excellent). */
  satisfactionScore: number | null;

  /** Quality of the platform instructions and UX (1=poor, 5=excellent). */
  platformQualityScore: number | null;

  /** Whether the platform instructions were clear. */
  instructionClarity: InstructionClarity | null;

  /** Would the operator do this mission again? */
  wouldDoAgain: WouldDoAgainResponse | null;

  /** Would the operator recommend this platform to others? */
  wouldRecommend: boolean | null;

  // ── Notes ─────────────────────────────────────────────────────────────────
  /** Free-text notes. Can be added during or after the session. */
  notes: string | null;

  // ── Auto-captured temporal fields ─────────────────────────────────────────
  /** [AUTO] Day of week: 0=Sunday, 6=Saturday. Derived from startedAt. */
  dayOfWeek: number | null;

  /** [AUTO] Hour of day: 0–23. Derived from startedAt. Best-time analysis. */
  hourOfDay: number | null;

  /** [AUTO] ISO week number (1–53). Derived from startedAt. Streak analysis. */
  weekNumber: number | null;

  /** [AUTO] Which sequential session this is today across all platforms (1-based). */
  sessionSequenceNumber: number;

  // ── Auto-captured device fields ────────────────────────────────────────────
  /** [AUTO] Browser name auto-detected from userAgent. Supplements deviceUsed. */
  browserDetected: string | null;

  /** [AUTO] Screen category auto-detected from window.innerWidth. */
  screenCategory: 'mobile' | 'tablet' | 'desktop' | null;

  // ── Metadata ──────────────────────────────────────────────────────────────
  /** True when created automatically (e.g. from MissionSourceCard action). False when manually created. */
  createdAutomatically: boolean;

  /** ISO 8601 — last time any field was changed. */
  lastModified: string;
}

// ─── Pre-mission questionnaire data ───────────────────────────────────────────

/** Data collected in the pre-mission questionnaire. */
export interface PreMissionData {
  mood:                  OperatorMood;
  timeAvailableMinutes:  number;          // 15 | 30 | 60 | 120
  deviceUsed:            SessionDevice;
  earningsExpectationMet: boolean;
}

// ─── Post-mission questionnaire data ─────────────────────────────────────────

/** Data collected in the post-mission questionnaire. */
export interface PostMissionData {
  status:               MissionSessionStatus;
  earningsActual:       number;
  durationMinutes:      number;
  paymentStatus:        PaymentStatus;
  difficultyScore:      number;           // 1–5
  platformQualityScore: number;           // 1–5
  instructionClarity:   InstructionClarity;
  wouldDoAgain:         WouldDoAgainResponse;
  wouldRecommend:       boolean;
  notes:                string | null;
}

// ─── Aggregated statistics ────────────────────────────────────────────────────

/** Aggregated per-company statistics derived exclusively from sessions. */
export interface CompanySessionStats {
  companyId:              string;
  companyName:            string;
  totalSessions:          number;
  lifetimeEarnings:       number;         // USD
  averageHourlyRate:      number | null;  // USD/hr
  highestHourlyRate:      number | null;  // USD/hr
  fastestCompletion:      number | null;  // minutes
  averageCompletionTime:  number | null;  // minutes
  /** Percentage of sessions that reached Completed or Applied status. */
  successRate:            number;         // 0–100
  /** Percentage of sessions that were NOT 'Did Not Qualify'. */
  qualificationRate:      number;         // 0–100
  averageSatisfaction:    number | null;  // 1–5
  averageDifficulty:      number | null;  // 1–5
  currentStreak:          number;         // consecutive days with a session
  longestStreak:          number;
  lastSession:            string | null;  // ISO 8601
  totalHoursWorked:       number;
  totalActiveSessions:    number;
}

/** Global operator statistics derived from all sessions. */
export interface OperatorSessionStats {
  todayEarnings:           number;
  weeklyEarnings:          number;
  monthlyEarnings:         number;
  lifetimeEarnings:        number;
  totalHoursWorked:        number;
  averageHourlyRate:       number | null;
  bestPlatform:            string | null;  // highest average hourly rate
  bestMissionType:         MissionSessionType | null;
  bestDay:                 string | null;  // e.g. "Tuesday"
  bestTimeOfDay:           string | null;  // e.g. "9am–12pm"
  longestStreak:           number;
  mostProfitablePlatform:  string | null;  // highest total earnings
  mostReliablePlatform:    string | null;  // highest qualification rate
  totalSessions:           number;
  thisWeekSessions:        number;
}

// ─── Storage ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nomad:sessions:v1';

function loadSessions(): MissionSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as MissionSession[]) : [];
  } catch {
    return [];
  }
}

function saveSessions(sessions: MissionSession[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    // LIVE PLATFORM: announce the mutation (legacy bridge → core bus).
    window.dispatchEvent(new CustomEvent('nomad:data-changed', { detail: { domain: 'sessions' } }));
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

// ─── Auto-capture helpers ─────────────────────────────────────────────────────

/** Returns 0–6 (Sunday=0) for the given ISO date string. */
function getDayOfWeek(iso: string): number {
  return new Date(iso).getDay();
}

/** Returns 0–23 for the given ISO date string. */
function getHourOfDay(iso: string): number {
  return new Date(iso).getHours();
}

/** Returns the ISO week number (1–53) for the given ISO date string. */
function getISOWeekNumber(iso: string): number {
  const d = new Date(iso);
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/** Auto-detect browser name from userAgent. */
function detectBrowser(): string | null {
  try {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox'))  return 'Firefox';
    if (ua.includes('Edg'))      return 'Edge';
    if (ua.includes('Chrome'))   return 'Chrome';
    if (ua.includes('Safari'))   return 'Safari';
    if (ua.includes('Opera'))    return 'Opera';
    return 'Unknown';
  } catch {
    return null;
  }
}

/** Auto-detect screen category from window width. */
function detectScreenCategory(): 'mobile' | 'tablet' | 'desktop' | null {
  try {
    const w = window.innerWidth;
    if (w < 768)  return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  } catch {
    return null;
  }
}

/** Auto-detect device from screen size. Used as initial value for pre-mission form. */
export function autoDetectDevice(): SessionDevice {
  try {
    const w = window.innerWidth;
    if (w < 768) return 'Mobile';
    if (w < 1024) return 'Tablet';
    return 'Desktop';
  } catch {
    return 'Unknown';
  }
}

/** Compute today's session sequence number for the given sessions array. */
function computeSessionSequence(existingSessions: MissionSession[]): number {
  const todayISO = new Date().toISOString().slice(0, 10);
  const todayCount = existingSessions.filter(s => s.startedAt.slice(0, 10) === todayISO).length;
  return todayCount + 1;
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

/**
 * Create a new Mission Session.
 * Called immediately when the operator confirms the pre-mission questionnaire.
 *
 * @param params   Core identity data — companyName, opportunityId, estimated earnings, mission type.
 * @param preMission  Data from the pre-mission questionnaire.
 * @param createdAutomatically  True when triggered by MissionSourceCard action.
 */
export function createSession(
  params: {
    companyName:       string | null;
    opportunityId:     number | null;
    earningsEstimated: number | null;
    missionType?:      MissionSessionType;
    operatorId?:       string;
  },
  preMission: PreMissionData,
  createdAutomatically = true,
): MissionSession {
  const sessions = loadSessions();
  const now = new Date().toISOString();
  const companyId = params.companyName ? params.companyName.toLowerCase().trim() : null;

  const session: MissionSession = {
    sessionId:             `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    companyId,
    companyName:           params.companyName,
    opportunityId:         params.opportunityId,
    operatorId:            params.operatorId ?? 'operator',
    startedAt:             now,
    endedAt:               null,
    durationMinutes:       null,
    status:                'Reviewing',    // Sprint 105.2: website visit, not work
    earningsActual:        null,
    earningsEstimated:     params.earningsEstimated,
    actualHourlyRate:      null,
    paymentStatus:         'Unknown',
    paymentReceivedDate:   null,
    qualificationResult:   'Reviewing',
    missionType:           params.missionType ?? 'Unknown',
    deviceUsed:            preMission.deviceUsed,
    location:              null,
    preSessionMood:        preMission.mood,
    timeAvailableMinutes:  preMission.timeAvailableMinutes,
    earningsExpectationMet: preMission.earningsExpectationMet,
    frictionScore:         null,
    difficultyScore:       null,
    satisfactionScore:     null,
    platformQualityScore:  null,
    instructionClarity:    null,
    wouldDoAgain:          null,
    wouldRecommend:        null,
    notes:                 null,
    dayOfWeek:             getDayOfWeek(now),
    hourOfDay:             getHourOfDay(now),
    weekNumber:            getISOWeekNumber(now),
    sessionSequenceNumber: computeSessionSequence(sessions),
    browserDetected:       detectBrowser(),
    screenCategory:        detectScreenCategory(),
    createdAutomatically,
    lastModified:          now,
  };

  sessions.push(session);
  saveSessions(sessions);
  return session;
}

/**
 * Update a session's status or mutable fields.
 * Returns null if the session is not found.
 * Refuses to modify terminal sessions (except to add notes).
 */
export function updateSession(
  sessionId: string,
  data: Partial<Pick<MissionSession,
    'status' | 'notes' | 'durationMinutes' | 'location' | 'missionType'
  >>,
): MissionSession | null {
  const sessions = loadSessions();
  const idx = sessions.findIndex(s => s.sessionId === sessionId);
  if (idx < 0) return null;

  const session = sessions[idx];
  const now = new Date().toISOString();

  // Only notes can be added to terminal sessions
  if (TERMINAL_STATUSES.includes(session.status) && data.status && data.status !== session.status) {
    return session; // status change refused on terminal session
  }

  sessions[idx] = { ...session, ...data, lastModified: now };
  saveSessions(sessions);
  return sessions[idx];
}

/**
 * Complete a session with post-mission questionnaire data.
 * Marks the session terminal, computes actualHourlyRate, sets endedAt.
 * Returns null if the session is not found.
 */
export function completeSession(
  sessionId: string,
  post: PostMissionData,
): MissionSession | null {
  const sessions = loadSessions();
  const idx = sessions.findIndex(s => s.sessionId === sessionId);
  if (idx < 0) return null;

  const session = sessions[idx];
  const now = new Date().toISOString();

  // Compute actual hourly rate
  const actualHourlyRate = post.durationMinutes > 0 && post.earningsActual > 0
    ? Math.round((post.earningsActual / (post.durationMinutes / 60)) * 100) / 100
    : null;

  sessions[idx] = {
    ...session,
    status:               post.status,
    qualificationResult:  post.status,
    endedAt:              now,
    durationMinutes:      post.durationMinutes,
    earningsActual:       post.earningsActual,
    actualHourlyRate,
    paymentStatus:        post.paymentStatus,
    difficultyScore:      post.difficultyScore,
    satisfactionScore:    post.platformQualityScore,   // platform quality = satisfaction
    platformQualityScore: post.platformQualityScore,
    instructionClarity:   post.instructionClarity,
    wouldDoAgain:         post.wouldDoAgain,
    wouldRecommend:       post.wouldRecommend,
    notes:                post.notes,
    lastModified:         now,
  };
  saveSessions(sessions);
  return sessions[idx];
}

/**
 * Add or append a note to an existing session.
 * Safe to call on any session status.
 */
export function addNoteToSession(sessionId: string, note: string): MissionSession | null {
  const sessions = loadSessions();
  const idx = sessions.findIndex(s => s.sessionId === sessionId);
  if (idx < 0) return null;

  const session = sessions[idx];
  const now = new Date().toISOString();
  const existing = session.notes ? session.notes.trim() : '';
  const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  sessions[idx] = {
    ...session,
    notes: existing ? `${existing}\n[${timestamp}] ${note.trim()}` : `[${timestamp}] ${note.trim()}`,
    lastModified: now,
  };
  saveSessions(sessions);
  return sessions[idx];
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/** Returns all sessions, newest first. */
export function getAllSessions(): MissionSession[] {
  return loadSessions().sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

/** Returns all non-terminal sessions (the operator is still "active" on them). */
export function getActiveSessions(): MissionSession[] {
  return loadSessions().filter(s => !TERMINAL_STATUSES.includes(s.status));
}

/** Returns all sessions for a given normalised company id, newest first. */
export function getSessionsByCompany(companyId: string): MissionSession[] {
  const key = companyId.toLowerCase().trim();
  return loadSessions()
    .filter(s => s.companyId === key)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

/** Returns the most recent active session (for status bar). Null if none. */
export function getMostRecentActiveSession(): MissionSession | null {
  const active = getActiveSessions();
  if (!active.length) return null;
  return active.sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
}

/**
 * Sprint 109: Parallel Mission Engine™
 * Returns the most recent non-terminal session for a given company, or null.
 * Use this for per-company session state in the multi-mission model.
 * Identity key: companyId is always companyName.toLowerCase().trim().
 */
export function getActiveSessionForCompany(companyId: string): MissionSession | null {
  const key = companyId.toLowerCase().trim();
  const active = getActiveSessions().filter(s => s.companyId === key);
  if (!active.length) return null;
  return active.sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
}

// ─── Per-company statistics ───────────────────────────────────────────────────

function avgOrNull(nums: number[]): number | null {
  if (!nums.length) return null;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
}

/** Compute the current streak (consecutive calendar days with at least one session). */
function computeCurrentStreak(sessions: MissionSession[]): number {
  if (!sessions.length) return 0;
  const days = new Set(sessions.map(s => s.startedAt.slice(0, 10)));
  const sorted = [...days].sort().reverse();
  const today = new Date().toISOString().slice(0, 10);
  let streak = 0;
  let cursor = new Date(today);
  for (const day of sorted) {
    const cursorStr = cursor.toISOString().slice(0, 10);
    if (day === cursorStr) {
      streak++;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
}

/** Compute the longest historical streak in days. */
function computeLongestStreak(sessions: MissionSession[]): number {
  if (!sessions.length) return 0;
  const days = [...new Set(sessions.map(s => s.startedAt.slice(0, 10)))].sort();
  let longest = 1;
  let current = 1;
  for (let i = 1; i < days.length; i++) {
    const prev = new Date(days[i - 1]);
    const curr = new Date(days[i]);
    const diff = (curr.getTime() - prev.getTime()) / 86_400_000;
    if (diff === 1) {
      current++;
      if (current > longest) longest = current;
    } else {
      current = 1;
    }
  }
  return longest;
}

/**
 * Compute aggregated statistics for one company from its sessions.
 * All values are derived — nothing is manually edited.
 */
export function computeCompanyStats(companyId: string): CompanySessionStats {
  const key = companyId.toLowerCase().trim();
  const all = loadSessions().filter(s => s.companyId === key);
  const terminal = all.filter(s => TERMINAL_STATUSES.includes(s.status));
  const completed = all.filter(s => s.status === 'Completed' || s.status === 'Applied');

  const hourlyRates  = all.filter(s => s.actualHourlyRate != null).map(s => s.actualHourlyRate!);
  const earnings     = all.filter(s => s.earningsActual   != null).map(s => s.earningsActual!);
  const durations    = all.filter(s => s.durationMinutes  != null && s.durationMinutes > 0).map(s => s.durationMinutes!);
  const satisfaction = all.filter(s => s.satisfactionScore != null).map(s => s.satisfactionScore!);
  const difficulty   = all.filter(s => s.difficultyScore  != null).map(s => s.difficultyScore!);

  const totalHoursWorked = durations.reduce((s, m) => s + m, 0) / 60;
  const lifetimeEarnings = earnings.reduce((s, e) => s + e, 0);
  const successRate      = terminal.length
    ? Math.round((completed.length / terminal.length) * 100) : 0;
  const dnqCount         = all.filter(s => s.status === 'Did Not Qualify').length;
  const qualificationRate = terminal.length
    ? Math.round(((terminal.length - dnqCount) / terminal.length) * 100) : 100;

  const lastSession = all.length
    ? all.map(s => s.startedAt).sort().at(-1) ?? null : null;

  return {
    companyId: key,
    companyName: all[0]?.companyName ?? key,
    totalSessions:          all.length,
    lifetimeEarnings,
    averageHourlyRate:      avgOrNull(hourlyRates),
    highestHourlyRate:      hourlyRates.length ? Math.max(...hourlyRates) : null,
    fastestCompletion:      durations.length   ? Math.min(...durations)   : null,
    averageCompletionTime:  avgOrNull(durations),
    successRate,
    qualificationRate,
    averageSatisfaction:    avgOrNull(satisfaction),
    averageDifficulty:      avgOrNull(difficulty),
    currentStreak:          computeCurrentStreak(all),
    longestStreak:          computeLongestStreak(all),
    lastSession,
    totalHoursWorked,
    totalActiveSessions:    all.filter(s => !TERMINAL_STATUSES.includes(s.status)).length,
  };
}

// ─── Global operator statistics ───────────────────────────────────────────────

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function isoDateOffsetsAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

/** Returns the ISO week number for today. */
function currentWeekNumber(): number {
  return getISOWeekNumber(new Date().toISOString());
}

/**
 * Compute global operator statistics from all sessions.
 * All values are derived — none are manually edited.
 */
export function computeOperatorStats(): OperatorSessionStats {
  const all        = loadSessions();
  const todayISO   = new Date().toISOString().slice(0, 10);
  const weekStart  = isoDateOffsetsAgo(new Date().getDay()); // start of this week (Sunday)
  const monthStart = new Date().toISOString().slice(0, 7);   // "YYYY-MM"
  const thisWeek   = currentWeekNumber();

  const withEarnings = all.filter(s => s.earningsActual != null && s.earningsActual > 0);

  const todayEarnings   = withEarnings.filter(s => s.startedAt.slice(0, 10) === todayISO).reduce((s, r) => s + r.earningsActual!, 0);
  const weeklyEarnings  = withEarnings.filter(s => s.startedAt.slice(0, 10) >= weekStart).reduce((s, r) => s + r.earningsActual!, 0);
  const monthlyEarnings = withEarnings.filter(s => s.startedAt.slice(0, 7) === monthStart).reduce((s, r) => s + r.earningsActual!, 0);
  const lifetimeEarnings = withEarnings.reduce((s, r) => s + r.earningsActual!, 0);

  const allDurations = all.filter(s => s.durationMinutes != null && s.durationMinutes > 0).map(s => s.durationMinutes!);
  const totalHoursWorked = allDurations.reduce((s, m) => s + m, 0) / 60;

  const allHourlyRates = all.filter(s => s.actualHourlyRate != null && s.actualHourlyRate > 0).map(s => s.actualHourlyRate!);
  const averageHourlyRate = avgOrNull(allHourlyRates);

  // Best platform by average hourly rate
  const platformRateMap = new Map<string, number[]>();
  const platformEarningsMap = new Map<string, number>();
  const platformQualMap = new Map<string, { total: number; qualified: number }>();

  for (const s of all) {
    if (!s.companyId) continue;
    if (s.actualHourlyRate != null) {
      const rates = platformRateMap.get(s.companyId) ?? [];
      rates.push(s.actualHourlyRate);
      platformRateMap.set(s.companyId, rates);
    }
    if (s.earningsActual != null) {
      platformEarningsMap.set(s.companyId, (platformEarningsMap.get(s.companyId) ?? 0) + s.earningsActual);
    }
    if (TERMINAL_STATUSES.includes(s.status)) {
      const current = platformQualMap.get(s.companyId) ?? { total: 0, qualified: 0 };
      current.total++;
      if (s.status !== 'Did Not Qualify') current.qualified++;
      platformQualMap.set(s.companyId, current);
    }
  }

  let bestPlatform: string | null = null;
  let bestRate = -Infinity;
  for (const [platform, rates] of platformRateMap) {
    const avg = rates.reduce((s, r) => s + r, 0) / rates.length;
    if (avg > bestRate) { bestRate = avg; bestPlatform = platform; }
  }

  let mostProfitablePlatform: string | null = null;
  let mostEarnings = -Infinity;
  for (const [platform, total] of platformEarningsMap) {
    if (total > mostEarnings) { mostEarnings = total; mostProfitablePlatform = platform; }
  }

  let mostReliablePlatform: string | null = null;
  let bestQualRate = -1;
  for (const [platform, { total, qualified }] of platformQualMap) {
    if (total < 2) continue; // need at least 2 sessions for reliability signal
    const rate = qualified / total;
    if (rate > bestQualRate) { bestQualRate = rate; mostReliablePlatform = platform; }
  }

  // Best mission type by average hourly rate
  const typeRateMap = new Map<string, number[]>();
  for (const s of all) {
    if (s.missionType && s.actualHourlyRate != null) {
      const rates = typeRateMap.get(s.missionType) ?? [];
      rates.push(s.actualHourlyRate);
      typeRateMap.set(s.missionType, rates);
    }
  }
  let bestMissionType: MissionSessionType | null = null;
  let bestTypeRate = -Infinity;
  for (const [type, rates] of typeRateMap) {
    const avg = rates.reduce((s, r) => s + r, 0) / rates.length;
    if (avg > bestTypeRate) { bestTypeRate = avg; bestMissionType = type as MissionSessionType; }
  }

  // Best day of week
  const dayRateMap = new Map<number, number[]>();
  for (const s of all) {
    if (s.dayOfWeek != null && s.actualHourlyRate != null) {
      const rates = dayRateMap.get(s.dayOfWeek) ?? [];
      rates.push(s.actualHourlyRate);
      dayRateMap.set(s.dayOfWeek, rates);
    }
  }
  let bestDay: string | null = null;
  let bestDayRate = -Infinity;
  for (const [day, rates] of dayRateMap) {
    const avg = rates.reduce((s, r) => s + r, 0) / rates.length;
    if (avg > bestDayRate) { bestDayRate = avg; bestDay = DAY_NAMES[day]; }
  }

  // Best time of day (3-hour blocks)
  const blockRateMap = new Map<number, number[]>(); // block = Math.floor(hour / 3)
  for (const s of all) {
    if (s.hourOfDay != null && s.actualHourlyRate != null) {
      const block = Math.floor(s.hourOfDay / 3);
      const rates = blockRateMap.get(block) ?? [];
      rates.push(s.actualHourlyRate);
      blockRateMap.set(block, rates);
    }
  }
  let bestTimeOfDay: string | null = null;
  let bestBlockRate = -Infinity;
  const blockLabels = ['12am–3am', '3am–6am', '6am–9am', '9am–12pm', '12pm–3pm', '3pm–6pm', '6pm–9pm', '9pm–12am'];
  for (const [block, rates] of blockRateMap) {
    const avg = rates.reduce((s, r) => s + r, 0) / rates.length;
    if (avg > bestBlockRate) { bestBlockRate = avg; bestTimeOfDay = blockLabels[block] ?? null; }
  }

  const thisWeekSessions = all.filter(s => s.weekNumber === thisWeek).length;

  return {
    todayEarnings,
    weeklyEarnings,
    monthlyEarnings,
    lifetimeEarnings,
    totalHoursWorked,
    averageHourlyRate,
    bestPlatform,
    bestMissionType,
    bestDay,
    bestTimeOfDay,
    longestStreak:          computeLongestStreak(all),
    mostProfitablePlatform,
    mostReliablePlatform,
    totalSessions:          all.length,
    thisWeekSessions,
  };
}

// ─── Learning Engine bridge ───────────────────────────────────────────────────

/**
 * Returns a summary of session intelligence for each platform.
 * Intended for consumption by the Learning Engine in a future sprint.
 * Read-only — does not write to Learning Engine storage.
 */
export interface SessionLearningData {
  platform:              string;
  averageHourlyRate:     number | null;
  qualificationRate:     number;        // 0–100
  averageDifficulty:     number | null;
  averageSatisfaction:   number | null;
  paymentReliability:    number;        // 0–100 (% with Immediate status)
  sampleSize:            number;
  lastSession:           string | null;
}

export function buildSessionLearningData(): SessionLearningData[] {
  const sessions = loadSessions();
  const platforms = new Set(sessions.map(s => s.companyId).filter((p): p is string => p !== null));
  const result: SessionLearningData[] = [];

  for (const platform of platforms) {
    const ps       = sessions.filter(s => s.companyId === platform);
    const terminal = ps.filter(s => TERMINAL_STATUSES.includes(s.status));
    const dnq      = terminal.filter(s => s.status === 'Did Not Qualify').length;
    const immediate = terminal.filter(s => s.paymentStatus === 'Immediate').length;

    result.push({
      platform,
      averageHourlyRate:  avgOrNull(ps.filter(s => s.actualHourlyRate != null).map(s => s.actualHourlyRate!)),
      qualificationRate:  terminal.length ? Math.round(((terminal.length - dnq) / terminal.length) * 100) : 100,
      averageDifficulty:  avgOrNull(ps.filter(s => s.difficultyScore != null).map(s => s.difficultyScore!)),
      averageSatisfaction: avgOrNull(ps.filter(s => s.satisfactionScore != null).map(s => s.satisfactionScore!)),
      paymentReliability: terminal.length ? Math.round((immediate / terminal.length) * 100) : 0,
      sampleSize:         ps.length,
      lastSession:        ps.map(s => s.startedAt).sort().at(-1) ?? null,
    });
  }

  return result;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Human-readable label for a session status. */
export function sessionStatusLabel(status: MissionSessionStatus): string {
  const labels: Record<MissionSessionStatus, string> = {
    // Legacy
    'Created':           'Created',
    'Pre-Mission':       'Pre-Mission',
    // Sprint 105.2 states
    'Reviewing':         'Reviewing',
    'Interested':        'Interested',
    'Applying':          'Applying',
    'Waiting':           'Waiting for Approval',
    'Submitted':         'Submitted',
    'Paid':              'Paid',
    'Not Interested':    'Not Interested',
    // Existing states
    'Working':           'Working',
    'Paused':            'Paused',
    'Resumed':           'Resumed',
    'Completed':         'Completed',
    'Did Not Qualify':   'Did Not Qualify',
    'No Work Available': 'No Work Available',
    'Applied':           'Applied',
    'Technical Problem': 'Technical Problem',
    'Abandoned':         'Abandoned',
  };
  return labels[status] ?? status;
}

/** Tailwind colour class for a session status badge. */
export function sessionStatusColour(status: MissionSessionStatus): string {
  switch (status) {
    case 'Completed':
    case 'Paid':              return 'text-emerald-400 border-emerald-500/40';
    case 'Applied':
    case 'Submitted':         return 'text-blue-400 border-blue-500/40';
    case 'Working':
    case 'Resumed':           return 'text-cyan-400 border-cyan-500/40';
    case 'Applying':          return 'text-violet-400 border-violet-500/40';
    case 'Waiting':           return 'text-yellow-400 border-yellow-500/40';
    case 'Interested':        return 'text-sky-400 border-sky-500/40';
    case 'Reviewing':
    case 'Pre-Mission':       return 'text-muted-foreground/60 border-border/40';
    case 'Did Not Qualify':   return 'text-orange-400 border-orange-500/40';
    case 'No Work Available': return 'text-yellow-400 border-yellow-500/40';
    case 'Paused':            return 'text-amber-400 border-amber-500/40';
    case 'Technical Problem': return 'text-red-400 border-red-500/40';
    case 'Not Interested':
    case 'Abandoned':         return 'text-muted-foreground border-border';
    default:                  return 'text-muted-foreground/60 border-border/50';
  }
}

/** Convert TimeAvailable label to minutes. */
export function timeAvailableToMinutes(t: TimeAvailable): number {
  switch (t) {
    case '15 min':   return 15;
    case '30 min':   return 30;
    case '1 hour':   return 60;
    case '2+ hours': return 120;
  }
}
